import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Button } from "~/common/components";
import Input from "~/common/components/ui/Input";
import { BUTTON_VARIANTS } from "~/common/constants";
import { ConsultationInputLayout } from "~/providers/components";
import { useCreateReimbursementReport, useGetPatient } from "~/providers/hooks";
import {
  initiateReimbursementReportSchema,
  InitiateReimbursementReportSchemaType,
  isZoomFormPending,
  PatientParams,
} from "~/providers/utils";
import { ConsultationReimbursementReport } from "~/providers/utils/types/consultation";
import { ZoomTranscriptStatusTag } from "./ZoomTranscriptStatusTag";

interface ReimbursementReportCreateFormProps {
  reportData?: ConsultationReimbursementReport;
}

export const ReimbursementReportCreateForm = ({
  reportData,
}: ReimbursementReportCreateFormProps) => {
  const { patientId } = useParams<keyof PatientParams>() as PatientParams;
  const { data: patient } = useGetPatient(patientId);
  const consultationId = patient?.consultation?.id;

  const { createReimbursementReportMutation, isPending } =
    useCreateReimbursementReport(consultationId);

  const isFormDisabled = Boolean(reportData && isZoomFormPending(reportData));
  const transcriptStatus = reportData?.zoom_transcript_status;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InitiateReimbursementReportSchemaType>({
    resolver: zodResolver(initiateReimbursementReportSchema),
    values: {
      zoom_meeting_id: reportData?.zoom_meeting_id ?? "",
    },
  });

  const onSubmit: SubmitHandler<InitiateReimbursementReportSchemaType> = (
    data,
  ) => {
    if (!data.zoom_meeting_id) return;
    createReimbursementReportMutation(data.zoom_meeting_id);
  };

  return (
    <form className="flex size-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <ConsultationInputLayout
        label="Zoom meeting ID"
        labelAccessory={
          transcriptStatus ? (
            <ZoomTranscriptStatusTag status={transcriptStatus} />
          ) : undefined
        }
      >
        <div className="flex items-start gap-2">
          <Input
            className="h-11"
            containerClassName="flex-1"
            inputClassName="h-full min-h-0"
            placeholder="Enter Zoom meeting ID or link"
            error={errors.zoom_meeting_id?.message}
            disabled={isFormDisabled}
            {...register("zoom_meeting_id")}
          />
          <Button
            type="submit"
            variant={BUTTON_VARIANTS.primary}
            disabled={isPending || isFormDisabled}
            isLoading={isPending}
            className="h-11 w-fit shrink-0 py-0"
          >
            Generate
          </Button>
        </div>
      </ConsultationInputLayout>
    </form>
  );
};
